import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServerTypes } from 'src/app/models/servers';
import { ApiService } from 'src/app/services/api.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpBaseService } from '../admin/cust-admin/http-base.service';

export interface VehicleInformation {
    brand: string;
    model: string;
    color: string;
    category: string;
    gvw: number;
    kerbWeight: number;
    numberPlate: string;
    countryCode: string;
}

export interface AddAnprRecord {
  anpRecordType: string;
  npId: string;
  info: string;
}

export interface AnprRecord extends AddAnprRecord {
  vehicleId: string;
  anprRecordId: string;
  count: number;
}

export type DuplicateStatus = 'Reject' | 'Approve';

export interface DuplicateVehicleInformation {
  count: number;
  countCanceled: number;
  countPaid: number;
  countryCode: string;
  countryId: number;
  npId: string;
  vcm: string;
  vehicleId: string;
  vrm: string;
  status?: DuplicateStatus;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleInformationService extends HttpBaseService {

  private readonly validCountries = ["SE", "NO", "DK", "GB", "NL", "LT", "IT", "DE", "LV", "FI", "PL", "FR"];

  private readonly anprEndpoint = 'v1/anpr';
  private readonly vehicleInfoEndpoint = 'v1/vehicles';
  private readonly vehicleInfoClassEndpoint = 'v1/vehicles/class';
  private readonly serverUrl = this.apiService.getCurentServer(ServerTypes.boapi);

  constructor(protected http: HttpClient, private apiService: ApiService) {
    super(http);
  }
    public getAnprRecordsByVrm(vrm: string, useCache: boolean = true): Observable<AnprRecord[]> {
      const anprUrl = `${this.serverUrl}${this.anprEndpoint}?vrm=${vrm}&usecache=${useCache}`;
        return this.get<{ anprRecords: AnprRecord[] }>(anprUrl).pipe(map((result: { anprRecords: AnprRecord[] }) => result?.anprRecords ?? []));
  }

    public getAnprRecords(npId: string, useCache: boolean = true):  Observable<AnprRecord[]> {
        const anprUrl = `${this.serverUrl}${this.anprEndpoint}?npid=${npId}&usecache=${useCache}`;
    return this.get<{ anprRecords: AnprRecord[] }>(anprUrl).pipe(map((result: { anprRecords: AnprRecord[] }) => result?.anprRecords ?? []));
  }

    // Obsolete, shall be replaced with anpr info
    public getVehicleClaimDataFromNpId(npId: string, useCache: boolean = true): Observable<DuplicateVehicleInformation[]> {
    if (!npId) {
      return of(null);
    }
        const vehicleUrl = `${this.serverUrl}${this.vehicleInfoEndpoint}/?vrm=${npId.substr(3, npId.length)}&count=true&usecache=${useCache}`;
    return this.get<DuplicateVehicleInformation[]>(vehicleUrl);
  }


  public getDuplicateVehicleInfoForVrm(vrm: string): Observable<DuplicateVehicleInformation[]> {
    if (!vrm) {
      return of([null]);
    }
    const vehicleUrl = `${this.serverUrl}${this.vehicleInfoEndpoint}/?vrm=${vrm}&count=true`;
    return this.get<DuplicateVehicleInformation[]>(vehicleUrl);
  }

  public getVehicleInfo(country: string, vrm: string): Observable<VehicleInformation> {
    if (!this.validCountry(country)) {
      return of(null);
    }
    let npId = `${(country ?? '').replace(/\s/gi, '')}-${(vrm ?? '').replace(/\s/gi, '')}`; // should be vcm-vrm whitout spaces but with dashes
    const vehicleUrl = `${this.serverUrl}${this.vehicleInfoClassEndpoint}/${npId}`;
    return this.get<VehicleInformation>(vehicleUrl);
  }

  public getVehicleClassFromNpId(npId: string): Observable<VehicleInformation> {
    if (!npId) {
      return of(null);
    }
    let idx = npId.indexOf("-");
    return this.getVehicleInfo(npId.substring(0, idx), npId.substring(idx + 1));
  }

  public deleteAnprRecord(anprId: string): Observable<any> {
    const anprUrl = `${this.serverUrl}${this.anprEndpoint}/${anprId}`;
    return this.delete<any>(anprUrl);
  }

  public addAnprRecord(record: AddAnprRecord): Observable<any> {
    const anprUrl = `${this.serverUrl}${this.anprEndpoint}`;
    return this.post<any>(anprUrl, record);
  }

  private validCountry(country: string): boolean {
    return this.validCountries.includes((country ?? '').toUpperCase());
  }
}
